'use client';

import { trans } from '@/i18n';
import clsx from 'clsx';
import Image from 'next/image';
import { useCallback, useEffect, useState } from 'react';
import HoverPrefetchLink from '../hover-prefetch-link';
import { Carousel, CarouselContent, CarouselItem } from '../ui/carousel';

const HomeCategory = () => {
  const [carouselApi, setCarouselApi] = useState(null);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const onSelect = useCallback(api => {
    if (!api) {
      return;
    }

    setCanScrollPrev(api.canScrollPrev());
    setCanScrollNext(api.canScrollNext());
  }, []);

  useEffect(() => {
    if (!carouselApi) {
      return;
    }

    onSelect(carouselApi);
    carouselApi.on('reInit', onSelect);
    carouselApi.on('select', onSelect);

    return () => {
      carouselApi?.off('select', onSelect);
    };
  }, [carouselApi, onSelect]);

  return (
    <div className='mt-4 lg_mt-[68px]'>
      <h1 className='text-center font-Shango text-[26px] leading-8 lg_text-[28px] lg_leading-[34px]'>
        {trans('SHOP BY CATEGORY')}
      </h1>
      <ul className='mt-5 grid grid-cols-3 gap-x-2.5 gap-y-5 px-2.5 lg_hidden'>
        {configs.map(({ href, src, label }, i) => {
          return (
            <li key={i}>
              <HoverPrefetchLink
                className='block'
                href={href}
              >
                <Image
                  alt={label}
                  width={112}
                  height={130}
                  className='h-auto w-full'
                  src={src}
                  quality={90}
                />
              </HoverPrefetchLink>

              <div className='mt-[5px] text-center'>
                <HoverPrefetchLink
                  href={href}
                  className='text-sm leading-5 underline'
                >
                  {label}
                </HoverPrefetchLink>
              </div>
            </li>
          );
        })}
      </ul>
      <div className='relative mx-auto mt-[30px] hidden max-w-site lg_block'>
        <Carousel
          setApi={setCarouselApi}
          opts={{
            dragFree: false,
          }}
        >
          <CarouselContent className='-ml-[30px]'>
            {configs.map(({ href, desktopSrc, label }, i) => {
              return (
                <CarouselItem
                  key={i}
                  className='basis-[340px] pl-[30px]'
                >
                  <HoverPrefetchLink
                    className='block w-[310px]'
                    href={href}
                  >
                    <Image
                      alt={label}
                      width={310}
                      height={410}
                      src={desktopSrc}
                      className='h-auto w-full'
                      quality={90}
                    />
                  </HoverPrefetchLink>

                  <div className='mt-[15px]  text-center'>
                    <HoverPrefetchLink
                      href={href}
                      className={clsx(
                        'relative text-xl  leading-[25px] lg_hover_text-80583F',
                        'after_content-[""]',
                        'after_absolute  after_left-0 after_top-full after_h-[1px] after_w-full after_-translate-y-1 after_bg-#403732 lg_hover_after_bg-80583F',
                      )}
                    >
                      {label}
                    </HoverPrefetchLink>
                  </div>
                </CarouselItem>
              );
            })}
          </CarouselContent>
        </Carousel>

        {canScrollPrev ? (
          <button
            type='button'
            className='absolute left-0 top-[205px] flex h-[60px] w-8 -translate-y-1/2 rotate-180 cursor-pointer items-center justify-center bg-white/50'
            onClick={() => {
              carouselApi?.scrollPrev();
            }}
            aria-label='scroll-next'
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='12'
              height='22'
              fill='none'
              viewBox='0 0 12 22'
            >
              <path
                stroke='#80583F'
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth='2'
                d='m1.68 21 9.23-10L1.68 1'
              />
            </svg>
          </button>
        ) : null}
        {canScrollNext ? (
          <button
            type='button'
            className='absolute right-0 top-[205px] flex h-[60px] w-8 -translate-y-1/2 cursor-pointer items-center justify-center bg-white/50'
            onClick={() => {
              carouselApi?.scrollNext();
            }}
            aria-label='scroll-prev'
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='12'
              height='22'
              fill='none'
              viewBox='0 0 12 22'
            >
              <path
                stroke='#80583F'
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth='2'
                d='m1.68 21 9.23-10L1.68 1'
              />
            </svg>
          </button>
        ) : null}
      </div>
    </div>
  );
};

const configs = [
  {
    href: '/wedding-dresses',
    src: `${process.env.NEXT_PUBLIC_BASE_PATH}/images/home/shop-by-brides-m.png`,
    desktopSrc: `${process.env.NEXT_PUBLIC_BASE_PATH}/images/home/shop-by-brides.png`,
    label: trans('Brides'),
  },
  {
    href: '/bridesmaid-dresses',
    src: `${process.env.NEXT_PUBLIC_BASE_PATH}/images/home/shop-by-bridesmaids-m.png`,
    desktopSrc: `${process.env.NEXT_PUBLIC_BASE_PATH}/images/home/shop-by-bridesmaids.png`,
    label: trans('Bridesmaids'),
  },
  {
    href: '/wedding-guest-dresses',
    src: `${process.env.NEXT_PUBLIC_BASE_PATH}/images/home/shop-by-wedding-guest-dresses-m.webp`,
    desktopSrc: `${process.env.NEXT_PUBLIC_BASE_PATH}/images/home/shop-by-wedding-guest-dresses.webp`,
    label: trans('Wedding Guest Dresses'),
  },
  {
    href: '/prom-dresses',
    src: `${process.env.NEXT_PUBLIC_BASE_PATH}/images/home/shop-by-prom-dresses-m.webp`,
    desktopSrc: `${process.env.NEXT_PUBLIC_BASE_PATH}/images/home/shop-by-prom-dresses.webp`,
    label: trans('Prom Dresses'),
  },
  {
    href: '/mother-of-the-bride-dresses',
    src: `${process.env.NEXT_PUBLIC_BASE_PATH}/images/home/shop-by-moms-m.png`,
    desktopSrc: `${process.env.NEXT_PUBLIC_BASE_PATH}/images/home/shop-by-moms.png`,
    label: trans('Moms'),
  },
  {
    href: '/dresses',
    src: `${process.env.NEXT_PUBLIC_BASE_PATH}/images/home/shop-by-dress-m.png`,
    desktopSrc: `${process.env.NEXT_PUBLIC_BASE_PATH}/images/home/shop-by-dress.png`,
    label: trans('Dress'),
  },
  {
    href: '/plus-size-dresses',
    src: `${process.env.NEXT_PUBLIC_BASE_PATH}/images/home/shop-by-plus-size-m.png`,
    desktopSrc: `${process.env.NEXT_PUBLIC_BASE_PATH}/images/home/shop-by-plus-size.png`,
    label: trans('Plus Size'),
  },
];

export default HomeCategory;
